import { Component, Prop, State, Listen, Host, h, Method } from '@stencil/core';
import { generateId } from '../../utils/functions';

/**
 * Button Dropdowns control overlays for displaying lists of links. These can be used to add contextual menus to different areas of an application.
 */
@Component({
  tag: 'som-button-dropdown',
  styleUrl: 'som-button-dropdown.scss',
  scoped: true,
})
export class SomButtonDropdown {
  popoverId = generateId('som-button-dropdown-popover');
  buttonId = generateId('som-button-dropdown');

  buttonEl: HTMLSomButtonElement;
  popoverEl: HTMLSomPopoverElement;

  /**
   * This property determines the button style.
   */
   @Prop() buttonType: 'default' | 'outline' | 'clear' = 'default';  

  /**
   * This attribute controls the color of the button.
   */
  @Prop() color: 'primary' | 'secondary' | 'white' | 'nav' | 'gray' | 'danger' = 'gray';

  /**
   * This attribute controls the size of the button.
   */
  @Prop() size?: 'sm' | 'md' | 'lg' | 'block' = 'md';

  /**
   * This property will toggle the open state.
   */
  @Prop({ mutable: true, reflect: true }) collapsed: boolean = true;

  /**
    * This property determines which icon to be placed. It should be used in conjunction with iconPosition.
    */
  @Prop() icon?: string;

  /**
   * This attribute controls the text displayed inside the button.
   */
  @Prop() buttonText: string = '';

  /**
   * This property is used to set the button as disabled.
   */
  @Prop() disabled = false;

  /**
   * This property can be used to set the ARIA label associated with this input (for screen readers).
   */
  @Prop() buttonAriaLabel = '';

  /**
   * This property controls the horizontal position of the overlay.
   */
  @Prop() horizontalPosition: 'left' | 'right' = 'left';

    /**
   * The property controls the vertical position of the overlay.
   */
  @Prop() verticalPosition: 'top' | 'bottom' = 'bottom';


  @State() expanded: boolean = false;

  @Listen('blur', { target: 'window', capture: true })
  handleWindowBlur(e) {
    if (this.buttonEl === document.activeElement) {
      // Focus is removed from the page (i.e. user views another browser tab). We don't want to trigger an input focus.
      e.stopPropagation();
      e.preventDefault();
    }
  }

  @Listen('focus', { target: 'window', capture: true })
  handleWindowFocus(e) {
    if (this.buttonEl === document.activeElement) {
      // Focus is given to the page (i.e. user comes back from another browser tab). We don't want to trigger an input focus.
      e.stopPropagation();
      e.preventDefault();
    }
  }

  /**
   * This method will open the popover.
   */
  @Method()
  open(): Promise<void> {
    this.collapsed = false;
    return this.popoverEl.open();
  }

  /**
   * This method will close the popover.
   */
  @Method()
  close(): Promise<void> {
    this.collapsed = true;
    return this.popoverEl.close();
  }

  handleButtonBlur() {
//    console.log('Button Blur - Not sure what to do here');
  }

  handleButtonClick() {
    this.expanded = !this.expanded;
    this.popoverEl.show = !this.popoverEl.show;
  }

  handlePopoverClose() {
    // Popover closed
  }

  componentDidLoad() {
    !this.collapsed && this.open();
  }

  render() {
    const ariaDisabled = this.disabled ? { 'aria-disabled': '' } : null;
    return (      
      <Host>
        <som-button
          id={this.buttonId}
          type="button"
          color={this.color}
          size={this.size}
          button-type={this.buttonType}
          disabled={this.disabled}
          icon-position={this.icon ? 'only' : null}
          icon={this.icon ? this.icon : null}
          aria-label={this.buttonAriaLabel}
          aria-expanded={`${this.expanded}`}
          aria-controls={this.popoverId}
          onClick={this.handleButtonClick.bind(this)}
          onBlur={this.handleButtonBlur.bind(this)}
          {...ariaDisabled}
        >
          {(!this.icon) ? [this.buttonText, <span aria-hidden="true" class="chevron-icon"><som-icon name="chevron-down" display="block"></som-icon></span>] : null }
        </som-button>
        <som-popover
          ref={ref => {
            this.popoverEl = ref;
          }}
          id={this.popoverId}
          for={this.buttonId}
          vertical-position={this.verticalPosition}
          horizontal-position={this.horizontalPosition}          
          relative-positioning
          onPopoverClosed={this.handlePopoverClose.bind(this)}
          class="som-button-dropdown"
        >
          <slot></slot>
        </som-popover>
      </Host>
    );
  }
}
